import { ActionTree } from 'vuex';
import httpClient from '@/httpClient/httpClient';
import { RootState, ResponseBody, ResponseDataProvider } from '@/types';
import { ICampaignCreativesState } from '@/types/campaign/creatives';
import ResponseDataValue, {
  ResponseDataProviderValue,
} from '@/utils/ResponseDataValue';
import { Notification } from 'uiv';
import i18n from '@/i18n';
import * as qs from 'qs';

const REQUEST_ERROR = i18n.t('notifications.errorRequest');
const MODERATION_SAVE_ERROR = i18n.t('notifications.errorSaveModeration');

const api: ActionTree<ICampaignCreativesState, RootState> = {
  async loadOtherCreatives({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().get(
        '/campaign/creative',
        {
          params: data,
        }
      );

      const responseDataProvider: ResponseDataProvider =
        ResponseDataProviderValue(response, true);
      commit('setOtherCreatives', responseDataProvider.data);
      return responseDataProvider;
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },
  async loadCreativeFormats({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().get(
        '/statistics/creative-formats',
        {
          params: Object.assign({}, data),
        }
      );
      const responseDataProvider: ResponseDataProvider =
        ResponseDataProviderValue(response, true);
      commit('setCreativeFormats', responseDataProvider.data);
      return responseDataProvider;
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },
  async loadCreativeTemplates({ commit }, data = {}) {
    try {
      const response: ResponseBody = await httpClient().get(
        '/campaign/creative-template',
        {
          params: Object.assign({}, data),
        }
      );
      const responseDataProvider: ResponseDataProvider =
        ResponseDataProviderValue(response, true);

      commit('setCreativeTemplates', responseDataProvider.data);
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },
  async loadShowcaseTemplates({ commit }, data = {}) {
    try {
      const response: ResponseBody = await httpClient().get(
        '/campaign/showcase-template',
        {
          params: Object.assign({}, data),
        }
      );
      const responseDataProvider: ResponseDataProvider =
        ResponseDataProviderValue(response, true);

      commit('setShowcaseTemplates', responseDataProvider.data);
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },

  async loadModerateCreatives({ commit }, data) {
    try {
      let fields = data.fields;
      let expand = data.expand;

      if (Array.isArray(data.fields)) {
        fields = data.fields.join(',');
      }

      if (Array.isArray(data.expand)) {
        expand = data.expand.join(',');
      }

      const response: ResponseBody = await httpClient().get(
        '/moderate/creative',
        {
          params: Object.assign({}, data.searchData, {
            fields,
            expand,
          }),
        }
      );

      const responseDataProvider: ResponseDataProvider =
        ResponseDataProviderValue(response, true);
      commit('setModerateCreatives', responseDataProvider.data);
      return responseDataProvider;
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },
  async exportModeratedCreatives(_, data) {
    try {
      const response: ResponseBody = await httpClient().get(
        '/moderate/creative/export',
        {
          params: data,
        }
      );
      return response.data;
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },
  async moderation(_, data) {
    try {
      const response: ResponseBody = await httpClient().post(
        '/moderate/creative/moderation',
        qs.stringify(data.formData)
      );
      return ResponseDataValue(response.data, true);
    } catch (e) {
      Notification.notify.error({
        title: MODERATION_SAVE_ERROR,
      });
      return ResponseDataValue((e as any).response.data, false);
    }
  },
  async approveCreative(_, data) {
    try {
      const response: ResponseBody = await httpClient().post(
        '/moderate/creative/approve',
        qs.stringify(data.formData)
      );
      return ResponseDataValue(response.data, true);
    } catch (e) {
      Notification.notify.error({
        title: MODERATION_SAVE_ERROR,
      });
      return ResponseDataValue((e as any).response.data, false);
    }
  },
  async rejectCreative(_, data) {
    try {
      const response: ResponseBody = await httpClient().post(
        '/moderate/creative/reject',
        qs.stringify(data.formData)
      );
      return ResponseDataValue(response.data, true);
    } catch (e) {
      Notification.notify.error({
        title: MODERATION_SAVE_ERROR,
      });
      return ResponseDataValue((e as any).response.data, false);
    }
  },
};

export default api;
